"use client";

import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import {
  getCircleActionVariantStyle,
  resolveCircleActionVariant,
  type CircleActionVariant
} from "../circle-action-variants";

/** Trayectorias irregulares — distancias y ángulos variados (sm / sidebar). */
const PARTICLE_SPECS_SM = [
  { px: 49, py: 11, delay: 0, size: 2 },
  { px: 14, py: 43, delay: 26, size: 2 },
  { px: -31, py: 38, delay: 51, size: 2 },
  { px: -53, py: 9, delay: 11, size: 2 },
  { px: -47, py: -19, delay: 38, size: 2 },
  { px: -8, py: -51, delay: 63, size: 2 },
  { px: 36, py: -41, delay: 18, size: 2 },
  { px: 54, py: -7, delay: 44, size: 2 },
  { px: 22, py: -33, delay: 72, size: 2 },
  { px: -42, py: -6, delay: 33, size: 2 },
  { px: -17, py: 29, delay: 57, size: 2 },
  { px: 41, py: 24, delay: 8, size: 2 },
] as const;

/** Trayectorias irregulares — versión md (más alcance). */
const PARTICLE_SPECS_MD = [
  { px: 71, py: 16, delay: 0, size: 2 },
  { px: 21, py: 62, delay: 24, size: 2 },
  { px: -46, py: 55, delay: 48, size: 2 },
  { px: -78, py: 13, delay: 10, size: 2 },
  { px: -69, py: -28, delay: 36, size: 2 },
  { px: -12, py: -74, delay: 60, size: 2 },
  { px: 53, py: -60, delay: 16, size: 2 },
  { px: 80, py: -10, delay: 42, size: 2 },
  { px: 33, py: -48, delay: 68, size: 2 },
  { px: -63, py: -9, delay: 30, size: 2 },
  { px: -25, py: 44, delay: 54, size: 2 },
  { px: 60, py: 35, delay: 6, size: 2 },
] as const;

export interface CircleActionParticleBurstProps {
  variant?: CircleActionVariant | string | null;
  /** @deprecated Usar `variant`. */
  tone?: CircleActionVariant | string | null;
  size?: "sm" | "md";
  className?: string;
}

export function CircleActionParticleBurst({
  variant: variantProp,
  tone,
  size = "sm",
  className,
}: CircleActionParticleBurstProps) {
  const resolved = resolveCircleActionVariant(variantProp ?? tone ?? undefined);
  const { fx } = getCircleActionVariantStyle(resolved);
  const specs = size === "sm" ? PARTICLE_SPECS_SM : PARTICLE_SPECS_MD;

  return (
    <span
      className={cn(
        "cab-fx-particles pointer-events-none absolute left-1/2 top-1/2 z-0 h-0 w-0 overflow-visible",
        className
      )}
      aria-hidden
    >
      {specs.map((spec, index) => (
        <span
          key={`${spec.px}-${spec.py}-${index}`}
          className="cab-fx-particle absolute rounded-full"
          style={
            {
              "--cab-particle-color":
                fx.particles[index % fx.particles.length],
              "--cab-px": `${spec.px}px`,
              "--cab-py": `${spec.py}px`,
              "--cab-p-size": `${spec.size}px`,
              "--cab-p-delay": `${spec.delay}ms`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}
